import { RETRIEVE_URL, SUGGEST_URL } from './constants';
import { AutofillSuggestion, AutofillFeatureSuggestion } from './types';

import { LngLat, LngLatLike } from '../LngLat';
import { LngLatBounds, LngLatBoundsLike } from '../LngLatBounds';
import { SessionToken, SessionTokenLike } from '../SessionToken';

import { handleNonOkRes } from '../MapboxError';
import { getFetch } from '../fetch';
import { queryParams } from '../utils/queryParams';

interface AccessTokenOptions {
  /**
   * The [Mapbox access token](https://docs.mapbox.com/help/glossary/access-token/) to use for all requests.
   */
  accessToken: string;
}

interface FetchOptions {
  /**
   * If specified, the connected {@link AbortController} can be used to
   * abort the current network request(s).
   *
   * This mechanism intentionally works in the same way as the
   * [`fetch` API](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API#aborting_a_fetch).
   *
   * Reference:
   * https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal#examples
   */
  signal?: AbortSignal;
}

interface SessionTokenOptions {
  /**
   * A customer-provided session token value, which groups a series of requests together for [billing purposes](https://docs.mapbox.com/api/search/search/#search-api-pricing).
   *
   * Reference:
   * https://docs.mapbox.com/api/search/search/#session-based-pricing
   */
  sessionToken: SessionTokenLike;
}

/**
 * @typedef AutofillOptions
 */
export interface AutofillOptions {
  /**
   * The [IETF language tag](https://en.wikipedia.org/wiki/IETF_language_tag) to be returned.
   *
   * If not specified, `en` will be used.
   */
  language: string;
  /**
   * An [ISO 3166 alpha-2 country code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) to be returned.
   *
   * If not specified, results will not be filtered by country.
   */
  country: string;
  /**
   * Limit results to only those contained within the supplied bounding box.
   */
  bbox: string | LngLatBoundsLike;
  /**
   * The number of results to return, up to `10`.
   */
  limit: string | number;
  /**
   * Bias the response to favor results that are closer to this location.
   *
   * When both {@link AutofillOptions#proximity} and {@link AutofillOptions#origin} are specified, `origin` is interpreted as the
   * target of a route, while `proximity` indicates the current user location.
   */
  proximity: string | LngLatLike;
}

/**
 * @typedef AutofillSuggestionResponse
 */
export interface AutofillSuggestionResponse {
  /**
   * The attribution data for results.
   */
  attribution?: string;
  /**
   * The returned suggestion objects.
   *
   * @see {@link Suggestion}
   */
  suggestions: AutofillSuggestion[];
}

/**
 * @typedef AutofillRetrieveResponse
 */
export interface AutofillRetrieveResponse {
  type: 'FeatureCollection';
  /**
   * The attribution data for results.
   */
  attribution?: string;
  /**
   * The returned feature objects.
   *
   * @see {@link FeatureSuggestion}
   */
  features: AutofillFeatureSuggestion[];
}

/**
 * A `MapboxAutofill` object is an application's main entrypoint to the
 * Mapbox Autofill API. The Mapbox Autofill API is an API similar to {@link MapboxSearch},
 * but targeted towards **address** autocomplete.
 *
 * Only address types are returned by the API.
 *
 * `MapboxAutofill` is focused on the two-step, interactive search experience. These steps are:
 *   1. {@link MapboxAutofill#suggest}: The user enters a search term, and a list of suggested results is returned with
 *     address data.
 *   2. {@link MapboxAutofill#retrieve}: The user selects a result from the list of suggested results, and the
 *     corresponding geographic coordinates are returned.
 *
 * A [Mapbox access token](https://docs.mapbox.com/help/glossary/access-token/) is required to use `MapboxAutofill`, and
 * other options may be specified either in the constructor or in the {@link MapboxAutofill#suggest} call.
 *
 * @class MapboxAutofill
 * @param {AutofillOptions} [options]
 * @param {string} [options.accessToken]
 */
export class MapboxAutofill {
  static defaults: Partial<AutofillOptions> = {
    language: 'en',
    proximity: 'ip'
  };

  /**
   * The [Mapbox access token](https://docs.mapbox.com/help/glossary/access-token/) to use for all requests.
   */
  accessToken: string;

  /**
   * Any default options ({@link AutofillOptions}) to be merged into options in the following methods:
   * - {@link MapboxAutofill#suggest}
   *
   * @type {AutofillOptions}
   */
  defaults: Partial<AutofillOptions>;

  constructor(options: Partial<AccessTokenOptions & AutofillOptions> = {}) {
    const { accessToken, ...defaults } = options;

    this.accessToken = accessToken;

    // Assign defaults to this.defaults.
    this.defaults = {
      ...MapboxAutofill.defaults,
      ...defaults
    };
  }

  /** @section {Methods} */

  /**
   * {@link MapboxAutofill#suggest} is "part one" of the two-step autofill experience, and includes
   * autofill information.
   *
   * Suggestion objects **do not include geographic coordinates**. To get the coordinates of the result, use {@link MapboxSearch#retrieve}.
   *
   * For tracking purposes, it is useful for any follow-up requests based on this suggestion to include same
   * {@link Suggestion#sessionToken} as the original request.
   *
   * If you'd like session tokens to be handled automatically, see {@link SearchSession}.
   *
   * @param {AutofillOptions} optionsArg
   * @param {SessionTokenLike} optionsArg.sessionToken
   * @param {AbortSignal} [optionsArg.signal]
   */
  async suggest(
    searchText: string,
    optionsArg: SessionTokenOptions & Partial<FetchOptions & AutofillOptions>
  ): Promise<AutofillSuggestionResponse> {
    if (!searchText) {
      throw new Error('searchText is required');
    }

    const { sessionToken, signal } = optionsArg;

    const options = {
      ...this.defaults,
      ...optionsArg,
      sessionToken
    };

    const url = new URL(`${SUGGEST_URL}/${encodeURIComponent(searchText)}`);
    url.search = this.#getQueryParams(options);

    const { fetch } = getFetch();
    const res = await fetch(url.toString(), {
      signal
    });

    // Throw custom error if status code is not 200.
    await handleNonOkRes(res);

    const json = (await res.json()) as AutofillSuggestionResponse;

    // Add 'original_search_text' so we can retrieve.
    return {
      ...json,
      suggestions: json.suggestions.map((suggestion) => {
        return {
          ...suggestion,
          original_search_text: searchText
        };
      })
    };
  }

  /**
   * {@link MapboxAutofill#retrieve} is "part two" of the two-step autofill experience and includes
   * geographic coordinates in [GeoJSON](https://docs.mapbox.com/help/glossary/geojson/) format.
   *
   * {@link suggestion} is usually a {@link AutofillSuggestion} returned from "part one,"
   * {@link MapboxAutofill#suggest}.
   *
   * **Legal terms:**
   *
   * Geographic coordinates should be used ephemerally and not persisted.
   *
   * This permanent policy is consistent with the [Mapbox Terms of Service](https://www.mapbox.com/tos/) and failure to comply
   * may result in modified or discontinued service.
   *
   * Additionally, the [Mapbox Terms of Service](https://www.mapbox.com/tos/) states any rendering of a feature suggestion
   * must be using Mapbox map services (for example, displaying results on Google Maps or MapKit JS is not allowed).
   *
   * **Disclaimer:**
   *
   * The failure of Mapbox to exercise or enforce any right or provision of these Terms will not constitute a waiver of such right or provision.
   *
   * @param {AutofillOptions} optionsArg
   * @param {SessionTokenLike} optionsArg.sessionToken
   * @param {AbortSignal} [optionsArg.signal]
   */
  async retrieve(
    suggestion: AutofillSuggestion,
    optionsArg: SessionTokenOptions & Partial<FetchOptions>
  ): Promise<AutofillRetrieveResponse> {
    if (!suggestion) {
      throw new Error('suggestion is required');
    }
    if (!this.canRetrieve(suggestion)) {
      throw new Error('suggestion cannot be retrieved');
    }

    const { sessionToken: sessionTokenLike, signal } = optionsArg;

    const sessionToken = SessionToken.convert(sessionTokenLike);

    const url = new URL(`${RETRIEVE_URL}/${suggestion.action.id}`);
    url.search = queryParams({
      access_token: this.accessToken,
      session_token: sessionToken.id
    });

    const { fetch } = getFetch();
    const res = await fetch(url.toString(), { signal });

    // Throw custom error if status code is not 200.
    await handleNonOkRes(res);

    const json = (await res.json()) as AutofillRetrieveResponse;
    return json;
  }

  /**
   * Returns the query parameters used by {@link MapboxAutofill#suggest}
   */
  #getQueryParams(
    options: Partial<AutofillOptions & SessionTokenOptions>
  ): string {
    return queryParams(
      {
        types: 'address',
        streets: true,
        access_token: this.accessToken,
        language: options.language,
        country: options.country,
        limit: options.limit
      },
      options.sessionToken && {
        session_token: SessionToken.convert(options.sessionToken).id
      },
      options.proximity && {
        proximity:
          typeof options.proximity === 'string'
            ? options.proximity
            : LngLat.convert(options.proximity).toArray().join(',')
      },
      options.bbox && {
        bbox:
          typeof options.bbox === 'string'
            ? options.bbox
            : LngLatBounds.convert(options.bbox).toFlatArray().join(',')
      }
    );
  }

  /**
   * Returns true if {@link MapboxAutofill#retrieve} can be called on this suggestion,
   * false otherwise.
   */
  canRetrieve(suggestion: AutofillSuggestion): boolean {
    const action = suggestion.action;

    return typeof action?.id === 'string';
  }
}
